import React, { useState, useMemo, useRef, useCallback } from 'react';
import useClickOutside from '../hooks/useClickOutside';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  DollarSign, User as UserIcon, Building2, Search, Filter, X, Check,
  ArrowUpDown, Clock, AlertTriangle, CheckSquare, ChevronDown, RotateCcw, Repeat, Reply
} from 'lucide-react';
import api, { organizationsAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import PipelineLeadDrawer from '../components/PipelineLeadDrawer';
import OpenDentalSyncBadge from '../components/OpenDentalSyncBadge';
import { useOpenDentalBooking } from '../contexts/OpenDentalBookingContext';

const SOURCE_LABELS = {
  website: 'Website', phone: 'Phone', email: 'Email',
  social_media: 'Social Media', business_card: 'Business Card',
  referral: 'Referral', google_ads: 'Google Ads', other: 'Other',
};
const PRIORITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };
const SORT_OPTIONS = [
  { value: 'recent',     label: 'Newest in stage' },
  { value: 'oldest',     label: 'Oldest in stage' },
  { value: 'value_desc', label: 'Highest value' },
  { value: 'value_asc',  label: 'Lowest value' },
  { value: 'stuck',      label: 'Stuck longest' },
  { value: 'name',       label: 'Name (A-Z)' },
];
const EMPTY_FILTERS = {
  search: '',
  owners: [],     // user ids; '__unassigned__' for unowned
  sources: [],    // lead source enum
  priorities: [], // low/medium/high
  tags: [],       // string tag values
  valueMin: '',
  valueMax: '',
  stuckDays: '',  // deals in current stage longer than N days
  reengaged: false, // only leads that submitted a new form (server/services/reengagement.js)
};

// "Submitted a new form" badge text for a re-engaged lead.
const reengagedLabel = (lead) => (lead.reengaged?.reopened ? 'Reopened · new form' : 'Re-engaged · new form');

const initialsOf = (name = '', email = '') => {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const colorFor = (id = '') => {
  const palette = ['bg-blue-100 text-blue-800', 'bg-emerald-100 text-emerald-800', 'bg-amber-100 text-amber-800', 'bg-purple-100 text-purple-800', 'bg-rose-100 text-rose-800', 'bg-cyan-100 text-cyan-800'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

// Meta-style urgency pill keyed off the lead's earliest open task dueDate:
// overdue (bold dark red) → today (red) → this week (amber) → anything
// further out (emerald, with a readable month label so the operator
// always sees *when* the next touch lands, not just that it's "later").
const DUE_PILL = (dueDate) => {
  if (!dueDate) return null;
  const startOf = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x.getTime(); };
  const today = startOf(new Date());
  const due = startOf(new Date(dueDate));
  const day = 24 * 3600 * 1000;
  if (due < today) {
    const days = Math.round((today - due) / day);
    return { label: `${days}d overdue`, cls: 'bg-red-100 text-red-900 border-red-300 font-semibold' };
  }
  if (due === today) return { label: 'Due today', cls: 'bg-red-50 text-red-700 border-red-200 font-medium' };
  if (due <= today + 6 * day) return { label: 'Due this week', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  const d = new Date(dueDate);
  const month = d.toLocaleString('en-US', { month: 'long' });
  const yearSuffix = d.getFullYear() === new Date().getFullYear() ? '' : ` ${d.getFullYear()}`;
  return { label: `Due in ${month}${yearSuffix}`, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
};

const CURRENCY_FALLBACK = 'USD';

const PipelineBoard = () => {
  const queryClient = useQueryClient();
  const { interceptWonTransition } = useOpenDentalBooking();
  const [includeClosed, setIncludeClosed] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortBy, setSortBy] = useState('recent');
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useClickOutside(showFilters, () => setShowFilters(false));
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  // Bulk selection: when on, card click toggles selection (instead of
  // opening the drawer) and a sticky bar at the bottom exposes Move /
  // Assign actions for the chosen leads. Drag-and-drop is disabled in
  // selection mode so the operator can't accidentally re-stage one card
  // while ticking others.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const { data: pipelines } = useQuery(
    ['pipelines'],
    () => api.get('/pipelines').then((r) => r.data)
  );
  const pipeline = pipelines?.[0];
  const pipelineId = pipeline?._id;

  // Per-stage scroll-driven lazy loading. /board returns 50 per stage
  // and this hook fetches the next slice when the column scrolls near
  // its bottom. A Set ref guards against overlapping requests for the
  // same stage. Declared after pipelineId so the useCallback deps
  // array doesn't trip the TDZ.
  const loadingStagesRef = useRef(new Set());
  const loadMoreForStage = useCallback(async (stage) => {
    if (!pipelineId) return;
    const id = String(stage._id);
    if (loadingStagesRef.current.has(id)) return;
    if ((stage.leads || []).length >= (stage.totalCount || 0)) return;
    loadingStagesRef.current.add(id);
    try {
      const r = await api.get(`/pipelines/${pipelineId}/stage/${id}/leads`, {
        params: { skip: (stage.leads || []).length, limit: 50, includeClosed },
      });
      const more = r.data?.leads || [];
      if (more.length === 0) return;
      // Write to the unfiltered cache key — lazy-load only runs when
      // the operator isn't searching, so the active board query's key
      // has q='' and matches this entry. Search-active sessions skip
      // lazy-load entirely (see scroll handler) so there's no risk of
      // overwriting the search subset with unfiltered rows.
      queryClient.setQueryData(['pipeline-board', pipelineId, { includeClosed, q: '' }], (prev) => {
        if (!prev) return prev;
        const stages = prev.stages.map((s) => {
          if (String(s._id) !== id) return s;
          // Dedupe by _id — Mongo skip+sort can overlap pages when
          // many leads share the same sort timestamps (bulk imports).
          // Stable tiebreaker on the server fixes the cause; this is
          // belt-and-suspenders so React doesn't trip on duplicate
          // keys if a stale page slips through.
          const seen = new Set((s.leads || []).map((l) => String(l._id)));
          const fresh = more.filter((l) => !seen.has(String(l._id)));
          if (fresh.length === 0) return s;
          return { ...s, leads: [...(s.leads || []), ...fresh] };
        });
        return { ...prev, stages };
      });
    } catch (_) {
      // swallow — the column just stops growing; user can retry by scrolling again
    } finally {
      loadingStagesRef.current.delete(id);
    }
  }, [pipelineId, includeClosed, queryClient]);

  // Search is now a server param so a hit on a lead that hasn't been
  // lazy-loaded into a stage's column still surfaces. Other filters
  // (owner, priority, source, tags, value, stuck days) remain
  // client-side over the loaded board state — those rarely need full
  // tenant coverage and the inline filter feel is snappier.
  const serverSearch = filters.search.trim();
  const boardQueryKey = ['pipeline-board', pipelineId, { includeClosed, q: serverSearch }];

  const { data: board, isLoading, error } = useQuery(
    boardQueryKey,
    () =>
      api
        .get(`/pipelines/${pipelineId}/board`, {
          params: { includeClosed, ...(serverSearch ? { q: serverSearch } : {}) },
        })
        .then((r) => r.data),
    {
      enabled: !!pipelineId,
      // Debounce keystrokes — without this every char fires a request.
      // 250ms feels responsive without spamming the server.
      keepPreviousData: true,
    }
  );

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { staleTime: 60_000 }
  );
  const userById = useMemo(() => {
    const m = new Map();
    for (const u of orgUsersResp?.data?.users || []) m.set(String(u._id), u);
    return m;
  }, [orgUsersResp]);

  const moveMutation = useMutation(
    ({ leadId, stageId }) =>
      api.put(`/leads/${leadId}`, { stageId }).then((r) => r.data),
    {
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Stage update failed');
        queryClient.invalidateQueries(boardQueryKey);
      },
      onSuccess: () => {
        // Success-toast skipped (noisy); the cache was updated optimistically.
        // Refresh so the AI follow-up badge / next check-in reflect the
        // server (the stage change pauses or resumes the sequence).
        queryClient.invalidateQueries(boardQueryKey);
      }
    }
  );

  // Bulk operations — single endpoint handles both "Move to stage" and
  // "Assign to owner". Server-side path validates org-scope and writes
  // an activity per lead, so the UI just needs to invalidate the board
  // afterwards.
  const bulkMutation = useMutation(
    ({ action, data }) =>
      api.post('/leads/bulk', { action, leadIds: Array.from(selectedIds), data }).then((r) => r.data),
    {
      onSuccess: (_, variables) => {
        const count = selectedIds.size;
        const verb = variables.action === 'updateStage' ? 'moved' : 'assigned';
        toast.success(`${count} lead${count === 1 ? '' : 's'} ${verb}`);
        exitSelectionMode();
        queryClient.invalidateQueries(boardQueryKey);
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || err.response?.data?.message || 'Bulk update failed');
      },
    }
  );

  // Pipeline filtering happens client-side. The board endpoint already
  // ships every visible lead per stage, so a useMemo over the in-memory
  // payload gives sub-frame response when typing in the search box.
  // Server-side filtering is overkill until orgs hit 1k+ deals on board.
  const reengagedCount = useMemo(
    () => (board?.stages || []).reduce((n, st) => n + (st.leads || []).filter((l) => l.reengagedAt).length, 0),
    [board]
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.search.trim()) n++;
    n += filters.owners.length;
    n += filters.sources.length;
    n += filters.priorities.length;
    n += filters.tags.length;
    if (filters.valueMin !== '') n++;
    if (filters.valueMax !== '') n++;
    if (filters.stuckDays !== '') n++;
    if (filters.reengaged) n++;
    return n;
  }, [filters]);

  // Tag inventory pulled from the visible board so the picker only shows
  // tags that are actually used in this org. Avoids paginating an
  // unbounded tag list from the server.
  const availableTags = useMemo(() => {
    const set = new Set();
    for (const stage of board?.stages || []) {
      for (const lead of stage.leads || []) {
        for (const t of lead.tags || []) {
          // Skip internal matchTags like 'meta:psid:xyz' — they're machine
          // identifiers, not human-meaningful labels.
          if (typeof t === 'string' && !/^(meta|google|tiktok):/.test(t)) set.add(t);
        }
      }
    }
    return Array.from(set).sort();
  }, [board]);

  const stageAgeDays = (lead) => {
    const start = lead.stageUpdatedAt ? new Date(lead.stageUpdatedAt) : new Date(lead.updatedAt || lead.createdAt);
    if (!start || isNaN(start.getTime())) return 0;
    return Math.floor((Date.now() - start.getTime()) / 86400000);
  };

  const matchesFilters = (lead) => {
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      const blob = [
        lead.firstName, lead.lastName, lead.email, lead.phone, lead.company,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!blob.includes(q)) return false;
    }
    if (filters.owners.length) {
      const aid = lead.assignedTo ? String(lead.assignedTo) : null;
      if (aid) {
        if (!filters.owners.includes(aid)) return false;
      } else {
        if (!filters.owners.includes('__unassigned__')) return false;
      }
    }
    if (filters.sources.length && !filters.sources.includes(lead.source)) return false;
    if (filters.priorities.length && !filters.priorities.includes(lead.priority)) return false;
    if (filters.tags.length) {
      const leadTags = lead.tags || [];
      if (!filters.tags.some((t) => leadTags.includes(t))) return false;
    }
    if (filters.valueMin !== '' && Number(lead.estimatedValue || 0) < Number(filters.valueMin)) return false;
    if (filters.valueMax !== '' && Number(lead.estimatedValue || 0) > Number(filters.valueMax)) return false;
    if (filters.stuckDays !== '' && stageAgeDays(lead) < Number(filters.stuckDays)) return false;
    if (filters.reengaged && !lead.reengagedAt) return false;
    return true;
  };

  const sortLeads = (leads) => {
    const arr = [...leads];
    switch (sortBy) {
      case 'recent':
        arr.sort((a, b) => stageAgeDays(a) - stageAgeDays(b));
        break;
      case 'oldest':
        arr.sort((a, b) => stageAgeDays(b) - stageAgeDays(a));
        break;
      case 'value_desc':
        arr.sort((a, b) => Number(b.estimatedValue || 0) - Number(a.estimatedValue || 0));
        break;
      case 'value_asc':
        arr.sort((a, b) => Number(a.estimatedValue || 0) - Number(b.estimatedValue || 0));
        break;
      case 'stuck':
        arr.sort((a, b) => stageAgeDays(b) - stageAgeDays(a));
        break;
      case 'name':
        arr.sort((a, b) => `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`));
        break;
      default: break;
    }
    // Re-engaged leads (new form from an existing lead) stay pinned to the
    // top of their column whatever the sort, newest first.
    const re = arr.filter((l) => l.reengagedAt).sort((a, b) => new Date(b.reengagedAt) - new Date(a.reengagedAt));
    return re.length ? [...re, ...arr.filter((l) => !l.reengagedAt)] : arr;
  };

  // Filtered + sorted view. We expose three counts per stage so the
  // header + lazy loader stay accurate:
  //   loadedCount  — how many rows the server has handed us so far
  //                  (drives the "X / total" display + the load-more
  //                  skip param)
  //   totalCount   — server's stage-wide total (unfiltered)
  //   visible      — the filtered+sorted slice we render as cards
  // totalValue follows visible so the rolled-up "$" number reflects
  // what the operator can actually see right now.
  const filteredStages = useMemo(() => {
    if (!board) return [];
    return board.stages.map((stage) => {
      const allLoaded = stage.leads || [];
      const visible = sortLeads(allLoaded.filter(matchesFilters));
      const totalValue = visible.reduce((s, l) => s + Number(l.estimatedValue || 0), 0);
      return {
        ...stage,
        leads: visible,
        loadedCount: allLoaded.length,
        totalCount: stage.totalCount, // server's stage-wide total
        totalValue,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, filters, sortBy]);

  const stats = useMemo(() => {
    const dealCount = filteredStages.reduce((sum, s) => sum + (s.totalCount || 0), 0);
    const totalValue = filteredStages.reduce((sum, s) => sum + (s.totalValue || 0), 0);
    return { dealCount, totalValue };
  }, [filteredStages]);

  const toggleFilter = (dim, value) => {
    setFilters((p) => {
      const cur = p[dim] || [];
      return { ...p, [dim]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  };
  const clearAllFilters = () => setFilters(EMPTY_FILTERS);
  const removeChip = (dim, value) => {
    if (Array.isArray(filters[dim])) setFilters((p) => ({ ...p, [dim]: p[dim].filter((v) => v !== value) }));
    else setFilters((p) => ({ ...p, [dim]: '' }));
  };

  const chipLabel = (dim, value) => {
    if (dim === 'owners') {
      if (value === '__unassigned__') return 'Owner: Unassigned';
      const u = userById.get(String(value));
      return `Owner: ${u?.name || u?.email || 'User'}`;
    }
    if (dim === 'sources')    return `Source: ${SOURCE_LABELS[value] || value}`;
    if (dim === 'priorities') return `Priority: ${PRIORITY_LABELS[value] || value}`;
    if (dim === 'tags')       return `Tag: ${value}`;
    if (dim === 'valueMin')   return `Min value: ${value}`;
    if (dim === 'valueMax')   return `Max value: ${value}`;
    if (dim === 'stuckDays')  return `Stuck > ${value}d`;
    if (dim === 'search')     return `"${value}"`;
    return `${dim}: ${value}`;
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStageId = destination.droppableId;

    // OpenDental: dropping a lead into a Won stage captures an appointment
    // first. interceptWonTransition resolves immediately ('proceed') unless
    // the org has the add-on configured, the stage is Won, and the lead
    // isn't already synced — in which case it opens the booking modal. If
    // the modal is cancelled we abort the move; since we never touched the
    // cache yet, the card just snaps back to its origin column.
    const board = queryClient.getQueryData(boardQueryKey);
    const toStage = board?.stages?.find((s) => String(s._id) === destination.droppableId);
    if (toStage?.closedType === 'won') {
      const movedLead = (board?.stages || [])
        .find((s) => String(s._id) === source.droppableId)
        ?.leads?.find((l) => String(l._id) === draggableId);
      const decision = await interceptWonTransition({ lead: movedLead, targetStage: toStage });
      if (decision === 'cancelled') return;
    }

    queryClient.setQueryData(boardQueryKey, (prev) => {
      if (!prev) return prev;
      const stages = prev.stages.map((s) => ({ ...s, leads: [...s.leads] }));
      const fromStage = stages.find((s) => String(s._id) === source.droppableId);
      const toStage = stages.find((s) => String(s._id) === destination.droppableId);
      if (!fromStage || !toStage) return prev;
      const idx = fromStage.leads.findIndex((l) => String(l._id) === draggableId);
      if (idx === -1) return prev;
      // Copy the card (don't mutate the cached object) so it re-renders.
      const moved = { ...fromStage.leads.splice(idx, 1)[0] };
      moved.stageId = toStage._id;
      // AI follow-up runs only in the org's follow-up stages: flip the
      // badge now, the refetch after the save fills in the details.
      if (moved.followUp) {
        const on = (prev.followUpStageIds || []).includes(String(toStage._id));
        moved.followUp = { ...moved.followUp, status: on ? 'active' : 'paused', nextAt: on ? moved.followUp.nextAt : null };
      }
      toStage.leads.splice(destination.index, 0, moved);
      if (source.droppableId !== destination.droppableId) {
        fromStage.totalCount = Math.max(0, (fromStage.totalCount || 1) - 1);
        fromStage.totalValue = Math.max(0, (fromStage.totalValue || 0) - (moved.estimatedValue || 0));
        toStage.totalCount = (toStage.totalCount || 0) + 1;
        toStage.totalValue = (toStage.totalValue || 0) + (moved.estimatedValue || 0);
      }
      return { ...prev, stages };
    });

    moveMutation.mutate({ leadId: draggableId, stageId: newStageId });
  };

  if (!pipelineId) return <div className="p-8 text-gray-500">Loading pipeline...</div>;
  if (isLoading) return <div className="p-8 text-gray-500">Loading board...</div>;
  if (error) return <div className="p-8 text-red-600">Failed to load board.</div>;

  const orgUsers = orgUsersResp?.data?.users || [];

  return (
    <div className="px-4 py-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{board.pipeline.name}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {stats.dealCount} deal{stats.dealCount !== 1 ? 's' : ''}
            {' · '}{formatCurrency(stats.totalValue, CURRENCY_FALLBACK)} total
            {activeFilterCount > 0 && <span className="ml-2 text-primary-700 font-medium">· filtered</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} />
            Show Closed
          </label>
          <button
            onClick={() => (selectionMode ? exitSelectionMode() : setSelectionMode(true))}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
              selectionMode
                ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <CheckSquare size={14} />
            {selectionMode ? 'Done' : 'Bulk Edit'}
          </button>
        </div>
      </div>

      {/* Filter / sort toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            placeholder="Search name, email, phone, company..."
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {filters.search && (
            <button
              onClick={() => setFilters((p) => ({ ...p, search: '' }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {reengagedCount > 0 && (
          <button
            type="button"
            onClick={() => setFilters((p) => ({ ...p, reengaged: !p.reengaged }))}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
              filters.reengaged ? 'bg-amber-100 text-amber-900 border-amber-300' : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
            }`}
            title="Leads already in your pipeline who submitted a new form"
          >
            <RotateCcw size={14} />
            Re-engaged
            <span className="ml-0.5 px-1.5 py-0.5 text-xs font-semibold bg-amber-500 text-white rounded-full">{reengagedCount}</span>
          </button>
        )}

        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
              activeFilterCount > 0
                ? 'bg-primary-50 text-primary-700 border-primary-200'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Filter size={14} />
            Filter
            {activeFilterCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-primary-600 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          {showFilters && (
            <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-[360px] max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Pipeline filters</h4>
                {activeFilterCount > 0 && (
                  <button onClick={clearAllFilters} className="text-xs text-gray-500 hover:text-gray-700">Clear all</button>
                )}
              </div>

              {/* Owner */}
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Owner</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  <CheckRow
                    checked={filters.owners.includes('__unassigned__')}
                    onClick={() => toggleFilter('owners', '__unassigned__')}
                    label={<span className="italic text-gray-500">Unassigned</span>}
                  />
                  {orgUsers.map((u) => (
                    <CheckRow
                      key={u._id}
                      checked={filters.owners.includes(String(u._id))}
                      onClick={() => toggleFilter('owners', String(u._id))}
                      label={<span className="truncate">{u.name || u.email}</span>}
                    />
                  ))}
                </div>
              </div>

              {/* Source */}
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Source</div>
                <div className="space-y-1">
                  {Object.entries(SOURCE_LABELS).map(([v, l]) => (
                    <CheckRow key={v} checked={filters.sources.includes(v)} onClick={() => toggleFilter('sources', v)} label={l} />
                  ))}
                </div>
              </div>

              {/* Priority */}
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Priority</div>
                <div className="space-y-1">
                  {Object.entries(PRIORITY_LABELS).map(([v, l]) => (
                    <CheckRow key={v} checked={filters.priorities.includes(v)} onClick={() => toggleFilter('priorities', v)} label={l} />
                  ))}
                </div>
              </div>

              {/* Tags */}
              {availableTags.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Tags</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {availableTags.map((t) => (
                      <CheckRow key={t} checked={filters.tags.includes(t)} onClick={() => toggleFilter('tags', t)} label={t} />
                    ))}
                  </div>
                </div>
              )}

              {/* Value range */}
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Estimated value</div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.valueMin}
                    onChange={(e) => setFilters((p) => ({ ...p, valueMin: e.target.value }))}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-md"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.valueMax}
                    onChange={(e) => setFilters((p) => ({ ...p, valueMax: e.target.value }))}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-md"
                  />
                </div>
              </div>

              {/* Stuck > N days */}
              <div className="mb-1">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1">
                  <AlertTriangle size={12} /> Stuck in current stage longer than
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    placeholder="e.g. 14"
                    value={filters.stuckDays}
                    onChange={(e) => setFilters((p) => ({ ...p, stuckDays: e.target.value }))}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-md"
                  />
                  <span className="text-xs text-gray-500">days</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-sm">
          <ArrowUpDown size={14} className="text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 -mt-1">
          {filters.search && (
            <Chip onRemove={() => removeChip('search', '')} label={chipLabel('search', filters.search)} />
          )}
          {filters.owners.map((v) => <Chip key={`owner-${v}`} onRemove={() => removeChip('owners', v)} label={chipLabel('owners', v)} />)}
          {filters.sources.map((v) => <Chip key={`src-${v}`} onRemove={() => removeChip('sources', v)} label={chipLabel('sources', v)} />)}
          {filters.priorities.map((v) => <Chip key={`pri-${v}`} onRemove={() => removeChip('priorities', v)} label={chipLabel('priorities', v)} />)}
          {filters.tags.map((v) => <Chip key={`tag-${v}`} onRemove={() => removeChip('tags', v)} label={chipLabel('tags', v)} />)}
          {filters.valueMin !== '' && <Chip onRemove={() => removeChip('valueMin', '')} label={chipLabel('valueMin', filters.valueMin)} />}
          {filters.valueMax !== '' && <Chip onRemove={() => removeChip('valueMax', '')} label={chipLabel('valueMax', filters.valueMax)} />}
          {filters.stuckDays !== '' && <Chip onRemove={() => removeChip('stuckDays', '')} label={chipLabel('stuckDays', filters.stuckDays)} />}
          {filters.reengaged && <Chip onRemove={() => setFilters((p) => ({ ...p, reengaged: false }))} label="Re-engaged only" />}
          <button onClick={clearAllFilters} className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline">Clear all</button>
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {filteredStages.map((stage) => (
            <div key={stage._id} className="flex-shrink-0 w-[300px] bg-gray-50 rounded-xl border border-gray-200 flex flex-col max-h-[calc(100vh-220px)]">
              <div
                className="px-4 py-3 rounded-t-xl border-b border-gray-200"
                style={{ backgroundColor: `${stage.color}15`, borderTopColor: stage.color, borderTopWidth: 3 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="font-semibold text-gray-800">{stage.name}</span>
                    <span className="text-xs text-gray-500" title={`${stage.loadedCount || 0} loaded of ${stage.totalCount || 0}`}>
                      ({(stage.loadedCount || 0) >= (stage.totalCount || 0)
                        ? (stage.totalCount || 0)
                        : `${stage.loadedCount || 0} / ${stage.totalCount || 0}`})
                    </span>
                  </div>
                  <span className="text-xs text-gray-600 font-medium">
                    {formatCurrency(stage.totalValue || 0, CURRENCY_FALLBACK)}
                  </span>
                </div>
              </div>

              <Droppable droppableId={String(stage._id)}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      // Trigger 200px before the actual bottom so the
                      // next batch is already in flight by the time the
                      // operator finishes the scroll. Pass the RAW
                      // stage (not the filtered slice) so the worker
                      // sees the true loaded count for the skip param.
                      // When search is active the server already
                      // returned every match — skip lazy-load so we
                      // don't pull unfiltered rows back in.
                      if (serverSearch) return;
                      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
                        const rawStage = board?.stages?.find((s) => String(s._id) === String(stage._id));
                        if (rawStage) loadMoreForStage(rawStage);
                      }
                    }}
                    className={`flex-1 overflow-y-auto p-2 space-y-2 transition-colors ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}
                  >
                    {stage.leads.length === 0 && !snapshot.isDraggingOver && (
                      <div className="text-center text-xs text-gray-400 py-8">
                        {activeFilterCount > 0 ? 'No matches in this stage' : 'No deals'}
                      </div>
                    )}
                    {stage.leads.map((lead, index) => (
                      <Draggable
                        key={lead._id}
                        draggableId={String(lead._id)}
                        index={index}
                        isDragDisabled={selectionMode}
                      >
                        {(prov, snap) => {
                          const isSelected = selectedIds.has(String(lead._id));
                          const onCardClick = () => {
                            if (snap.isDragging) return;
                            if (selectionMode) toggleSelected(String(lead._id));
                            else setDrawerLeadId(lead._id);
                          };
                          return (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            role="button"
                            tabIndex={0}
                            onClick={onCardClick}
                            onKeyDown={(e) => {
                              if ((e.key === 'Enter' || e.key === ' ') && !snap.isDragging) {
                                e.preventDefault();
                                onCardClick();
                              }
                            }}
                            className={`bg-white rounded-lg border p-3 text-sm cursor-pointer hover:shadow-md transition-all ${
                              isSelected
                                ? 'border-primary-500 ring-2 ring-primary-200 bg-primary-50/40'
                                : 'border-gray-200 hover:border-primary-300'
                            } ${snap.isDragging ? 'shadow-lg rotate-1' : ''}`}
                          >
                            <div className="flex items-start gap-2">
                              {selectionMode && (
                                <span
                                  className={`mt-0.5 w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                                    isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 bg-white'
                                  }`}
                                  aria-hidden="true"
                                >
                                  {isSelected && <Check size={12} className="text-white" />}
                                </span>
                              )}
                              <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 block truncate">
                              {`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '(no name)'}
                            </div>
                            {lead.reengagedAt && (
                              <div
                                className="mt-1 inline-flex max-w-full items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900"
                                title={`${lead.reengaged?.campaign ? `${lead.reengaged.campaign} · ` : ''}${new Date(lead.reengagedAt).toLocaleString()}${lead.reengaged?.reopened && lead.reengaged?.fromStage ? ` · was in ${lead.reengaged.fromStage}` : ''}`}
                              >
                                <RotateCcw className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{reengagedLabel(lead)}</span>
                              </div>
                            )}
                            {lead.followUpReply && (
                              <div
                                className="mt-1 inline-flex max-w-full items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800"
                                title={`Answered the ${lead.followUpReply.day != null ? `Day ${lead.followUpReply.day} ` : ''}AI check-in${lead.followUpReply.channel ? ` by ${lead.followUpReply.channel.toUpperCase()}` : ''} · ${new Date(lead.followUpReply.at).toLocaleString()}. Stays until someone on your team replies, calls, or moves the lead to another stage.`}
                              >
                                <Reply className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">Replied to follow-up</span>
                              </div>
                            )}
                            {lead.followUp?.status === 'active' && (
                              <div
                                className="mt-1 inline-flex max-w-full items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-800"
                                title={`AI follow-up running · step ${lead.followUp.step} of ${lead.followUp.total}${lead.followUp.nextAt ? ` · next ${new Date(lead.followUp.nextAt).toLocaleString()}` : ''}. Moving the lead to a stage without follow-ups pauses it.`}
                              >
                                <Repeat className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">AI follow-up · {lead.followUp.step}/{lead.followUp.total}</span>
                              </div>
                            )}
                            {lead.company && (
                              <div className="flex items-center gap-1 text-xs text-gray-600 mt-1 truncate">
                                <Building2 className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{lead.company}</span>
                              </div>
                            )}
                            {(lead.estimatedValue || lead.source) && (
                              <div className="mt-2">
                                {lead.estimatedValue ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                                    <DollarSign className="w-3 h-3" />
                                    {formatCurrency(lead.estimatedValue, lead.currency || CURRENCY_FALLBACK)}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500 truncate">
                                    {SOURCE_LABELS[lead.source] || lead.source}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Bottom row: urgency pill on the left, owner
                                avatar on the right. Always rendered so the
                                avatar stays bottom-aligned even when the
                                lead has no open task (pill is null). */}
                            <div className="flex items-end justify-between mt-2 gap-2 min-h-[24px]">
                              <div className="flex items-center gap-1 flex-wrap">
                                <OpenDentalSyncBadge status={lead.opendental?.syncStatus} />
                                {(() => {
                                  const pill = DUE_PILL(lead.nextTaskDueDate);
                                  if (!pill) return null;
                                  return (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] ${pill.cls}`}>
                                      <Clock className="w-3 h-3" />
                                      {pill.label}
                                    </span>
                                  );
                                })()}
                              </div>
                              {(() => {
                                const u = userById.get(String(lead.assignedTo));
                                if (!u) return null;
                                const label = u.name || u.email;
                                return (
                                  <span
                                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold flex-shrink-0 ${colorFor(String(u._id))}`}
                                    title={label}
                                  >
                                    {initialsOf(u.name, u.email)}
                                  </span>
                                );
                              })()}
                            </div>
                              </div>
                            </div>
                          </div>
                          );
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {drawerLeadId && (
        <PipelineLeadDrawer
          leadId={drawerLeadId}
          orgUsers={orgUsers}
          stages={board?.stages || []}
          onClose={() => setDrawerLeadId(null)}
        />
      )}

      {selectionMode && selectedIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          stages={board?.stages || []}
          orgUsers={orgUsers}
          onMove={(stageId) => bulkMutation.mutate({ action: 'updateStage', data: { stageId } })}
          onAssign={(assignedTo) => bulkMutation.mutate({ action: 'assignTo', data: { assignedTo } })}
          onClear={exitSelectionMode}
          isPending={bulkMutation.isLoading}
        />
      )}
    </div>
  );
};

// Sticky bottom toolbar — only renders when selection mode is on AND
// at least one card is ticked. Two dropdowns (Move to / Assign to)
// share the same animation logic via a tiny PickerMenu helper below.
const BulkActionBar = ({ selectedCount, stages, orgUsers, onMove, onAssign, onClear, isPending }) => {
  const [openMenu, setOpenMenu] = useState(null); // 'stage' | 'owner' | null
  const containerRef = useClickOutside(!!openMenu, () => setOpenMenu(null));
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
      <div
        ref={containerRef}
        className="flex items-center gap-2 bg-gray-900 text-white rounded-xl shadow-2xl border border-gray-800 px-4 py-2.5"
      >
        <span className="text-sm font-medium">
          {selectedCount} selected
        </span>
        <span className="w-px h-5 bg-gray-700" />
        <PickerMenu
          label="Move to"
          isOpen={openMenu === 'stage'}
          onToggle={() => setOpenMenu((m) => (m === 'stage' ? null : 'stage'))}
          disabled={isPending}
          items={(stages || []).map((s) => ({
            key: String(s._id),
            label: (
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: s.color || '#888' }} />
                {s.name}
              </span>
            ),
            onClick: () => { setOpenMenu(null); onMove(String(s._id)); },
          }))}
        />
        <PickerMenu
          label="Assign to"
          isOpen={openMenu === 'owner'}
          onToggle={() => setOpenMenu((m) => (m === 'owner' ? null : 'owner'))}
          disabled={isPending}
          items={[
            { key: '__unassign__', label: <span className="italic text-gray-500">Unassign</span>, onClick: () => { setOpenMenu(null); onAssign(null); } },
            ...(orgUsers || []).map((u) => ({
              key: String(u._id),
              label: u.name || u.email,
              onClick: () => { setOpenMenu(null); onAssign(String(u._id)); },
            })),
          ]}
        />
        <button
          onClick={onClear}
          disabled={isPending}
          className="ml-1 p-1.5 text-gray-400 hover:text-white disabled:opacity-50"
          aria-label="Cancel selection"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const PickerMenu = ({ label, items, isOpen, onToggle, disabled }) => (
  <div className="relative">
    <button
      onClick={onToggle}
      disabled={disabled}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-md"
    >
      {label}
      <ChevronDown size={14} />
    </button>
    {isOpen && (
      <div className="absolute bottom-full left-0 mb-2 w-56 bg-white text-gray-800 rounded-lg shadow-xl border border-gray-200 py-1 max-h-64 overflow-y-auto">
        {items.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No options</div>
        )}
        {items.map((it) => (
          <button
            key={it.key}
            onClick={it.onClick}
            className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            {it.label}
          </button>
        ))}
      </div>
    )}
  </div>
);

// Reusable inline checkbox row used inside the filter popover. Keeps the
// JSX compact and consistent across every dimension (owner / source /
// priority / tag).
const CheckRow = ({ checked, onClick, label }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 text-left"
  >
    <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
      checked ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
    }`}>
      {checked && <Check size={12} className="text-white" />}
    </span>
    <span className="text-sm text-gray-700 truncate">{label}</span>
  </button>
);

// Active-filter pill shown in the chips strip; clicking removes the
// individual filter without touching the others.
const Chip = ({ label, onRemove }) => (
  <button
    onClick={onRemove}
    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded-full hover:bg-primary-100"
  >
    <span>{label}</span>
    <X size={10} />
  </button>
);

export default PipelineBoard;
